/**
 * ProductMessaging — renders promotional text, variant info, and SFCC
 * promotion callouts below the product tile price.
 *
 * Ported from:
 * - hec-composable/hec/components/product-tile-cms/productMessaging.tsx
 * - hec-composable/hec/components/product-tile-cms/utils.ts (extractProductVariantsInfo)
 * - sfcc-website/org_hec/cartridge/templates/default/product/components/tilePromotions.isml
 *
 * Takes props directly (no ProductContext dependency) so it works in any tile context.
 */
import { Link } from "react-router";

import { useSite, useURL } from "~/contexts";
import type { HecProductPromotion } from "~/hec/types/product";
import { isScheduled } from "~/hec/types/product";
import { rewriteSfccContentLinks } from "~/hec/utils/sfcc-content-links";

/**
 * Narrow structural shape `ProductMessaging` actually reads. Both `HecProduct`
 * (PLP tile) and `C_ProductInfo` (cart Edit dialog) satisfy it, so callers
 * don't need cross-type casts. Keep the fields here in sync when the
 * component starts using a new prop.
 */
export interface ProductMessagingProduct {
  url?: string;
  /**
   * Narrow shape — the helper reads only `[0]?.name` + the values' count and
   * per-value `orderable`. Both `HecVariationAttribute` (PLP tile) and
   * `Product`'s `VariationAttribute` (cart Edit dialog) satisfy this.
   */
  variationAttributes?: Array<{
    name?: string;
    values?: Array<{ orderable?: boolean }>;
  }>;
  c_plpPromotions?: string;
  c_plpPromotionsDisplayFrom?: string;
  c_plpPromotionsDisplayTo?: string;
  c_crossPromoMessaging?: string;
  c_crossPromoMessagingDisplayFrom?: string;
  c_crossPromoMessagingDisplayTo?: string;
  promotions?: HecProductPromotion[];
  productPromotions?: HecProductPromotion[];
  /**
   * Server-resolved from `c_compareToSKU` (PLP loader — plp-shared-utils.server.ts).
   * Drives the "Compare to Caring Mill …" upsell callout (SFCC-11885).
   */
  compareToData?: { name: string; url: string; formattedPrice: string };
}

interface ProductMessagingProps {
  product: ProductMessagingProduct;
  "data-testid"?: string;
  /**
   * Suppress the trailing "More Options Available" variant-info link.
   * Used by the cart Edit dialog where the variant picker is rendered inline.
   */
  hideVariantInfo?: boolean;
}

// Strip trusted BM HTML tags to compare on text content. Shared by the promo
// de-dup (trackMsg) and the SFCC-11690 compare-to filter below.
const stripHtml = (html: string): string => html.replace(/<[^>]*>/g, "").trim();

// Polarity discriminator for the compare-to callout (SFCC-11885): a target name
// containing "Caring Mill" is the private-label upsell we KEEP; anything else is
// the outdated national-brand copy. Used by the render gate + the name formatter.
const CARING_MILL_NAME_RE = /caring\s*mill/i;

// SFCC-11690: the outdated "Compare to {brand}" Caring Mill callout is no longer
// shown on the live site. An earlier fix removed the separate compareToData /
// c_compareToSKU block, but on these tiles the copy is actually authored inside
// the BM-managed marketing HTML fields (c_crossPromoMessaging, and defensively
// c_plpPromotions) — a different source that still rendered it. Suppress a
// message whose text (HTML-stripped) STARTS with "Compare to" so legitimate
// cross-promo / PLP-promo copy on other products is unaffected. Scoped to these
// data-authored fields only — the SFCC promotion-engine callouts are active
// promotions and are left untouched.
const isOutdatedCompareTo = (html: string): boolean =>
  /^\s*compare to\b/i.test(stripHtml(html));

export function ProductMessaging({
  product,
  "data-testid": dataTestId,
  hideVariantInfo = false,
}: ProductMessagingProps) {
  const url = useURL();
  const { site } = useSite();
  const isFsa = site.id === "FSASTORE";
  const isHsa = site.id === "HSASTORE";

  const plpPromotionsText =
    isScheduled(
      product.c_plpPromotionsDisplayFrom,
      product.c_plpPromotionsDisplayTo,
    ) &&
    product.c_plpPromotions &&
    !isOutdatedCompareTo(product.c_plpPromotions)
      ? product.c_plpPromotions
      : "";

  const crossPromoText =
    isScheduled(
      product.c_crossPromoMessagingDisplayFrom,
      product.c_crossPromoMessagingDisplayTo,
    ) &&
    product.c_crossPromoMessaging &&
    !isOutdatedCompareTo(product.c_crossPromoMessaging)
      ? product.c_crossPromoMessaging
      : "";

  const variantInfo = extractProductVariantsInfo(product.variationAttributes);

  const rawPromotions = product.promotions ?? product.productPromotions;

  // Deduplicate all promo messages across every source (c_plpPromotions,
  // c_crossPromoMessaging, promotions[].calloutMsg/details). SFCC frequently
  // stores the same string in multiple places (e.g. "Excluded from all
  // promotions" appears in both custom attrs and the engine callout).
  // Strip HTML tags to compare text content so minor markup differences don't
  // cause the same message to render twice.
  // Call order is load-bearing: plpPromo wins over crossPromo wins over engine
  // callouts. First caller with a given text claims the slot; duplicates return
  // false and are suppressed. Set is recreated each render — no state leaks.
  const seenMsgs = new Set<string>();
  function trackMsg(html: string): boolean {
    const text = stripHtml(html);
    if (!text || seenMsgs.has(text)) return false;
    seenMsgs.add(text);
    return true;
  }

  const visiblePlpPromo =
    plpPromotionsText && trackMsg(plpPromotionsText) ? plpPromotionsText : "";
  const visibleCrossPromo =
    crossPromoText && trackMsg(crossPromoText) ? crossPromoText : "";
  const promotions = rawPromotions?.filter(promo => {
    const msg = promo.calloutMsg || promo.details;
    return !!msg && trackMsg(msg);
  });

  return (
    // `contents` keeps the children as direct siblings of the tile body (no
    // layout box) while propagating the brand font to the promo / variant-callout
    // text, which otherwise inherits the system default (Workers has no global
    // `font-body`; the composable applies it on `.sf-app`). SFCC-11626.
    <div className="contents font-grotesque">
      {/* PLP Promotions — date-gated HTML from custom attribute */}
      {visiblePlpPromo && (
        <div
          className="mt-2 text-sm text-tile-promo"
          dangerouslySetInnerHTML={{
            __html: rewriteSfccContentLinks(visiblePlpPromo),
          }}
          data-testid={dataTestId || "productMessaging-plpPromo"}
        />
      )}

      {/* Cross-promo messaging — date-gated HTML, site-specific */}
      {visibleCrossPromo && (
        <div
          className="mt-2 text-sm"
          dangerouslySetInnerHTML={{
            __html: rewriteSfccContentLinks(visibleCrossPromo),
          }}
          data-testid={dataTestId || "productMessaging-crossPromo"}
        />
      )}

      {/* Compare-to callout — brand-aware (SFCC-11885, re-fixing SFCC-11690).
          KEEP the "Compare to Caring Mill …" upsell (private-label, still live);
          the outdated "Compare to {national brand}" copy is dropped elsewhere —
          the structured block is gated to Caring-Mill targets, and the BM
          free-text fields are filtered via `isOutdatedCompareTo`. Sourced from
          `c_compareToSKU` → `compareToData` (resolved per tile in
          plp-shared-utils.server.ts). FSA + HSA get the branded leaf-mark
          callout; WDH gets plain inline text. The PDP renders its own
          CompareToMessage. Position mirrors the pre-SFCC-11690 original (after
          the promo blocks); promo-vs-compare-to ordering is owned by SFCC-11710. */}
      {product.compareToData &&
        CARING_MILL_NAME_RE.test(product.compareToData.name) &&
        (isFsa || isHsa ? (
          <Link
            to={url(product.compareToData.url)}
            prefetch="intent"
            className="mt-2 inline-block rounded-lg bg-compare-badge px-2.5 pb-1.5 pt-[9px] text-sm leading-[normal] hover:no-underline md:block md:px-2 md:pb-[3px] md:pt-1.5"
            data-testid={dataTestId || "productMessaging-compareTo"}
          >
            <img
              src="https://assets-manager.abtasty.com/90f64213b29a76219569f9b9a8d26cf1/account/caringmill-mark.svg"
              alt=""
              aria-hidden="true"
              className="mb-1 inline-block h-4 w-4"
            />{" "}
            Compare to {formatFsaCompareToName(product.compareToData.name)}{" "}
            <span className="font-extrabold">
              {product.compareToData.formattedPrice}
            </span>
          </Link>
        ) : (
          <div
            className="mt-2 text-sm"
            data-testid={dataTestId || "productMessaging-compareTo"}
          >
            Compare to{" "}
            <Link
              to={url(product.compareToData.url)}
              className="underline"
              prefetch="intent"
            >
              {product.compareToData.name}
            </Link>{" "}
            at {product.compareToData.formattedPrice}
          </div>
        ))}

      {/* SFCC promotion engine callouts — above variant info, matching SFRA order */}
      {promotions && promotions.length > 0 && (
        <div className="mt-2">
          {promotions.map((promo: HecProductPromotion) => {
            // Both fields are trusted BM HTML (SFRA renders both with encoding="off").
            // Prefer calloutMsg; fall back to details — never render both.
            const msg = promo.calloutMsg || promo.details;
            if (!msg) return null;
            return (
              <div
                key={promo.promotionId}
                className="text-sm"
                data-testid={dataTestId || "productMessaging-promo"}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: rewriteSfccContentLinks(msg),
                  }}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Variant info link — "More Options Available" / "X sizes Available" */}
      {!hideVariantInfo && variantInfo && product.url && (
        <Link
          to={url(product.url)}
          className="mt-2 block text-sm underline"
          prefetch="intent"
          data-testid={dataTestId || "productMessaging-variantInfo"}
        >
          {variantInfo}
        </Link>
      )}
    </div>
  );
}

/**
 * Trim a Caring Mill product name to the compact compare-to format:
 * "Caring Mill™ Aspirin… Tablets, 100 ct." → "Caring Mill® 100ct."
 *
 * Renders the registered-trademark glyph regardless of how the name is stored
 * (™ in SCAPI). The count is the last comma-separated segment with the unit
 * space collapsed (`100 ct.` → `100ct.`). Falls back to the full name when the
 * compare-to isn't a Caring Mill product (defensive — the render is already
 * gated on a Caring Mill target, so this only guards direct callers).
 */
function formatFsaCompareToName(fullName: string): string {
  if (!CARING_MILL_NAME_RE.test(fullName)) return fullName;
  const lastComma = fullName.lastIndexOf(",");
  if (lastComma === -1) return "Caring Mill®";
  const tail = fullName
    .slice(lastComma + 1)
    .trim()
    .replace(/(\d+)\s+(ct\.?|oz\.?|fl\s*oz\.?)/i, "$1$2");
  return `Caring Mill® ${tail}`;
}

// ---------------------------------------------------------------------------
// Utility: extract variant info text
// ---------------------------------------------------------------------------

function extractProductVariantsInfo(
  variationAttributes?: ProductMessagingProduct["variationAttributes"],
): string {
  const attrs = variationAttributes ?? [];
  const attrCount = attrs.length;
  // Count only buyable values. SCAPI declares value-level `orderable`
  // optional, so a missing flag must not zero the count.
  const firstValuesCount = (attrs[0]?.values ?? []).filter(
    v => v.orderable !== false,
  ).length;
  const firstName = attrs[0]?.name ?? "";

  if (attrCount > 0 && firstValuesCount > 1) {
    if (attrCount > 1) {
      return "More Options Available";
    }
    return `${firstValuesCount} ${firstName}s Available`;
  }
  return "";
}
